/**
 * Theme-aware color definitions for light/dark mode compatibility.
 * These colors are designed to work well in both light and dark themes.
 *
 * Usage:
 * - Store the color ID (e.g., 'blue', 'green') in the database
 * - Use resolveThemeColor() to get the correct hex value for the current theme
 * - Use ThemeAwareColorTiles component for color selection UI
 */

export interface ThemeAwareColor {
	id: string;
	name: string;
	light: string;
	dark: string;
}

/**
 * Curated set of 12 colors that work well in both light and dark themes.
 * Light mode uses Tailwind 500-level colors, dark mode uses 400-level colors.
 */
export const THEME_AWARE_COLORS: Array<ThemeAwareColor> = [
	{
		id: 'default-text',
		name: 'Default Text',
		light: '#111827',
		dark: '#F9FAFB',
	},
	{ id: 'blue', name: 'Blue', light: '#3B82F6', dark: '#60A5FA' },
	{ id: 'green', name: 'Green', light: '#10B981', dark: '#34D399' },
	{ id: 'purple', name: 'Purple', light: '#8B5CF6', dark: '#A78BFA' },
	{ id: 'amber', name: 'Amber', light: '#F59E0B', dark: '#FBBF24' },
	{ id: 'red', name: 'Red', light: '#EF4444', dark: '#F87171' },
	{ id: 'pink', name: 'Pink', light: '#EC4899', dark: '#F472B6' },
	{ id: 'cyan', name: 'Cyan', light: '#06B6D4', dark: '#22D3EE' },
	{ id: 'gray', name: 'Gray', light: '#6B7280', dark: '#9CA3AF' },
	{ id: 'teal', name: 'Teal', light: '#14B8A6', dark: '#2DD4BF' },
	{ id: 'orange', name: 'Orange', light: '#F97316', dark: '#FB923C' },
	{ id: 'lime', name: 'Lime', light: '#84CC16', dark: '#A3E635' },
];

/**
 * Default color ID to use when no color is selected
 */
export const DEFAULT_THEME_COLOR_ID = 'blue';

/**
 * Get a ThemeAwareColor by its ID.
 * Returns undefined if not found.
 */
export function getThemeAwareColorById(
	colorId: string,
): ThemeAwareColor | undefined {
	return THEME_AWARE_COLORS.find((c) => c.id === colorId);
}

/**
 * Check if a value is a legacy hex color (starts with #).
 */
export function isLegacyHexColor(value: string | null | undefined): boolean {
	if (value == null) {
		return false;
	}

	return value.startsWith('#');
}

/**
 * Resolve a color value (ID or legacy hex) to the appropriate hex value for the current theme.
 *
 * @param colorValue - Color ID (e.g., 'blue') or legacy hex (e.g., '#3B82F6')
 * @param isDark - Whether dark mode is active
 * @returns The hex color value to use
 *
 * @example
 * // New color ID - adapts to theme
 * resolveThemeColor('blue', false) // '#3B82F6' (light mode)
 * resolveThemeColor('blue', true)  // '#60A5FA' (dark mode)
 *
 * // Legacy hex - returns as-is
 * resolveThemeColor('#FF0000', false) // '#FF0000'
 * resolveThemeColor('#FF0000', true)  // '#FF0000'
 */
export function resolveThemeColor(
	colorValue: string | null | undefined,
	isDark: boolean,
): string | null {
	// No value - return null to allow fallback
	if (colorValue == null || colorValue === '') {
		return null;
	}

	// Legacy hex value - return as-is (no theme adaptation)
	if (colorValue.startsWith('#')) {
		return colorValue;
	}

	// Find color by ID
	const color = getThemeAwareColorById(colorValue);

	if (color == null) {
		// Unknown ID - return null to allow fallback
		return null;
	}

	return isDark ? color.dark : color.light;
}

/**
 * Get the first unused color ID from a list of used colors.
 * Useful for auto-assigning colors to new items.
 */
export function getNextAvailableColorId(
	usedColorIds: Array<string | null | undefined>,
): string {
	const usedSet = new Set(usedColorIds.filter((id) => id != null));

	for (const color of THEME_AWARE_COLORS) {
		if (!usedSet.has(color.id)) {
			return color.id;
		}
	}

	// All colors used, return the first one
	return THEME_AWARE_COLORS[0].id;
}
